import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { fmtDate, SIGN_STATUS } from "@/lib/labels";
import { IdCard, FileText, Eye, Download, Send, Trash2, PenLine, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";

const nok = (v) => (v || v === 0) ? `${Number(v).toLocaleString("no-NO")} kr` : "—";

const COMPANIES = ["Murphy AS", "Murphy Legal AS", "Murphy Regnskap AS", "Murphy Revisjon AS"];

function GenerateContractDialog({ userId, hr, leaders = [], onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({});
  useEffect(() => {
    if (open) setF({
      company: "Murphy AS", employment_type: "fast", temp_until: "", temp_duration: "",
      manager: "", task1: "", task2: "", task3: "", workplace: hr?.location || "",
      weekly_hours: "37,5", daily_hours: "7,5", pause_min: "30", time_from: "08.00", time_to: "16.00",
      schedule: "er fast", flextime: "i henhold til gjeldende intern ordning", probation: true, probation_months: "seks",
      overtime: "Etter arbeidsmiljøloven", bonus: "Ikke avtalt", other_allowances: "ingen",
      vacation_text: "25 virkedager etter ferieloven", feriepenger_pct: "12",
      pension_provider: "Duvi", pension_desc: "innskuddsbasert tjenestepensjon", injury_insurer: "",
      other_insurance: "ingen", social_benefits: "ingen", tariff: false, tariff_name: "",
      notice_months: "tre", salary_review: "", include_vedlegg1: false, v1_purpose: "", v1_responsibilities: "",
      v1_authority: "", v1_competence: "", v1_goals: "",
    });
  }, [open, hr]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const inp = (label, key, props = {}) => (
    <div><Label className="label-caps">{label}</Label>
      <Input value={f[key] ?? ""} onChange={(e) => set(key, e.target.value)} className="mt-1 bg-secondary border-border" data-testid={`gc-${key}`} {...props} /></div>
  );
  const gen = async () => {
    setBusy(true);
    try { await api.post(`/employees/${userId}/contracts/generate`, f); toast.success("Arbeidsavtale generert"); setOpen(false); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="secondary" data-testid="generate-contract">Generer arbeidsavtale</Button></DialogTrigger>
      <DialogContent className="bg-popover border-border max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Generer arbeidsavtale</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2 mb-2">Navn, stilling, stillingsbrøk, lønn, tiltredelse, adresse, kontonummer og fødselsdato hentes fra ansattkortet. Fyll ut resten under.</p>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-caps">Arbeidsgiverselskap</Label>
              <Select value={f.company} onValueChange={(v) => set("company", v)}>
                <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="gc-company"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border">{COMPANIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="label-caps">Ansettelsestype</Label>
              <Select value={f.employment_type} onValueChange={(v) => set("employment_type", v)}>
                <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="gc-employment_type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border"><SelectItem value="fast">Fast</SelectItem><SelectItem value="midlertidig">Midlertidig</SelectItem></SelectContent>
              </Select></div>
          </div>
          {f.employment_type === "midlertidig" && (
            <div className="grid grid-cols-2 gap-3">
              {inp("Varer til", "temp_until", { type: "date" })}
              {inp("Forventet varighet", "temp_duration")}
            </div>
          )}
          <div><Label className="label-caps">Nærmeste leder</Label>
            <Select value={f.manager} onValueChange={(v) => set("manager", v)}>
              <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="gc-manager"><SelectValue placeholder={leaders.length ? "Velg leder" : "Ingen ledere – huk av «Er leder» på ansattkort"} /></SelectTrigger>
              <SelectContent className="bg-popover border-border">
                {leaders.map((l) => <SelectItem key={l.id} value={l.hr?.position_title ? `${l.name} (${l.hr.position_title})` : l.name}>{l.name}{l.hr?.position_title ? ` · ${l.hr.position_title}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 gap-2">
            <Label className="label-caps">Hovedoppgaver</Label>
            {inp("Oppgave 1", "task1")}{inp("Oppgave 2", "task2")}{inp("Oppgave 3", "task3")}
          </div>
          {inp("Arbeidssted (kontor/adresse)", "workplace")}
          <div className="grid grid-cols-3 gap-3">
            {inp("Timer/uke", "weekly_hours")}{inp("Timer/dag", "daily_hours")}{inp("Pause (min)", "pause_min")}
            {inp("Fra kl.", "time_from")}{inp("Til kl.", "time_to")}
            <div><Label className="label-caps">Arbeidstid</Label>
              <Select value={f.schedule} onValueChange={(v) => set("schedule", v)}>
                <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="gc-schedule"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border"><SelectItem value="er fast">Fast</SelectItem><SelectItem value="kan variere etter arbeidsplan">Variabel</SelectItem></SelectContent>
              </Select></div>
          </div>
          <label className="flex items-center gap-2 text-sm" data-testid="gc-probation">
            <Checkbox checked={f.probation} onCheckedChange={(v) => set("probation", !!v)} data-testid="gc-probation-check" />
            Prøvetid (huk av hvis den ansatte skal ha prøvetid)
          </label>
          <div className="grid grid-cols-2 gap-3">
            {f.probation && inp("Prøvetid (mnd, tekst)", "probation_months")}
            {inp("Oppsigelsesfrist (mnd, tekst)", "notice_months")}
            {inp("Dato for årlig lønnsregulering (valgfri)", "salary_review")}
            {inp("Ferie", "vacation_text")}
            {inp("Feriepenger %", "feriepenger_pct")}
            {inp("Overtid", "overtime")}
            {inp("Bonus/provisjon", "bonus")}
            {inp("Andre tillegg", "other_allowances")}
            {inp("Pensjonsleverandør", "pension_provider")}
            {inp("Pensjonsordning", "pension_desc")}
            {inp("Yrkesskadeforsikring", "injury_insurer")}
            {inp("Andre forsikringer", "other_insurance")}
            {inp("Andre sosiale ytelser", "social_benefits")}
          </div>
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.tariff} onCheckedChange={(v) => set("tariff", !!v)} data-testid="gc-tariff" /> Regulert av tariffavtale</label>
          {f.tariff && inp("Tariffavtalens navn", "tariff_name")}
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.include_vedlegg1} onCheckedChange={(v) => set("include_vedlegg1", !!v)} data-testid="gc-vedlegg1" /> Ta med Vedlegg 1 – Stillingsbeskrivelse</label>
          {f.include_vedlegg1 && (
            <div className="space-y-2 pl-1 border-l-2 border-border">
              <div><Label className="label-caps">Formål med stillingen</Label><Textarea value={f.v1_purpose} onChange={(e) => set("v1_purpose", e.target.value)} rows={2} className="mt-1 bg-secondary border-border" /></div>
              <div><Label className="label-caps">Hovedansvar</Label><Textarea value={f.v1_responsibilities} onChange={(e) => set("v1_responsibilities", e.target.value)} rows={2} className="mt-1 bg-secondary border-border" /></div>
              <div><Label className="label-caps">Fullmakter og myndighet</Label><Textarea value={f.v1_authority} onChange={(e) => set("v1_authority", e.target.value)} rows={2} className="mt-1 bg-secondary border-border" /></div>
              <div><Label className="label-caps">Kompetanse-/autorisasjonskrav</Label><Textarea value={f.v1_competence} onChange={(e) => set("v1_competence", e.target.value)} rows={2} className="mt-1 bg-secondary border-border" /></div>
              <div><Label className="label-caps">Mål og forventninger</Label><Textarea value={f.v1_goals} onChange={(e) => set("v1_goals", e.target.value)} rows={2} className="mt-1 bg-secondary border-border" /></div>
            </div>
          )}
          <Button onClick={gen} disabled={busy} data-testid="gc-submit" className="w-full bg-white text-black hover:bg-white/90">{busy ? "Genererer…" : "Generer arbeidsavtale"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function openDoc(path) {
  api.get(path, { responseType: "blob" }).then((r) => {
    const url = URL.createObjectURL(r.data);
    window.open(url, "_blank");
  }).catch((e) => toast.error(apiError(e)));
}

export default function EmployeeCard({ user }) {
  const [open, setOpen] = useState(false);
  const [emp, setEmp] = useState(null);
  const [hr, setHr] = useState({});
  const [contracts, setContracts] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [leaderId, setLeaderId] = useState("");
  const [busy, setBusy] = useState(false);
  const [deps, setDeps] = useState([]);
  const [locs, setLocs] = useState([]);
  const [poss, setPoss] = useState([]);

  const load = useCallback(() => {
    api.get(`/employees/${user.id}`).then((r) => { setEmp(r.data); setHr(r.data.hr || {}); }).catch(() => {});
    api.get(`/employees/${user.id}/contracts`).then((r) => setContracts(r.data)).catch(() => {});
    api.get("/employees").then((r) => setLeaders(r.data.filter((x) => x.hr?.is_leader || ["admin", "management"].includes(x.role)))).catch(() => {});
    api.get("/departments").then((r) => setDeps(r.data)).catch(() => {});
    api.get("/locations").then((r) => setLocs(r.data)).catch(() => {});
    api.get("/positions").then((r) => setPoss(r.data)).catch(() => {});
  }, [user.id]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const saveHr = async () => {
    setBusy(true);
    try {
      const num = (v) => (v !== "" && v != null ? Number(v) : null);
      const payload = {
        position_title: hr.position_title || null, position_pct: num(hr.position_pct),
        annual_salary: num(hr.annual_salary), start_date: hr.start_date || null,
        end_date: hr.end_date || null, address: hr.address || null,
        postal_code: hr.postal_code || null, postal_place: hr.postal_place || null,
        residence: hr.residence || null,
        department: hr.department || null, location: hr.location || null,
        national_id: hr.national_id || null, bank_account: hr.bank_account || null,
        private_email: hr.private_email || null,
        vacation_days: num(hr.vacation_days), is_leader: !!hr.is_leader,
        emergency_contact_name: hr.emergency_contact_name || null,
        emergency_contact_phone: hr.emergency_contact_phone || null,
        emergency_contact_relation: hr.emergency_contact_relation || null,
        salary_review_date: hr.salary_review_date || null,
      };
      await api.put(`/employees/${user.id}/hr`, payload);
      toast.success("Ansattkort lagret"); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const send = async (eid) => {
    if (!leaderId) { toast.error("Velg leder som skal motsignere"); return; }
    try { const { data } = await api.post(`/employment/${eid}/send-signing`, { leader_id: leaderId }); toast.success(`Sendt til ${data.sent_to} for signering`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const countersign = async (eid) => {
    try { const { data } = await api.post(`/employment/${eid}/countersign`); toast.success(data.completed ? "Arbeidsavtale ferdig signert" : "Motsignert"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (eid) => { try { await api.delete(`/employment/${eid}`); toast.success("Slettet"); load(); } catch (e) { toast.error(apiError(e)); } };

  const field = (label, key, props = {}) => (
    <div><Label className="label-caps">{label}</Label>
      <Input value={hr[key] ?? ""} onChange={(e) => setHr({ ...hr, [key]: e.target.value })} className="mt-1 bg-secondary border-border" data-testid={`hr-${key}`} {...props} /></div>
  );

  const selectField = (label, key, options) => (
    <div><Label className="label-caps">{label}</Label>
      <Select value={hr[key] || ""} onValueChange={(v) => setHr({ ...hr, [key]: v })}>
        <SelectTrigger className="mt-1 bg-secondary border-border" data-testid={`hr-${key}`}><SelectValue placeholder="Velg" /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select></div>
  );

  const posNames = poss.map((p) => p.name);
  const posGroups = {};
  poss.forEach((p) => { const a = p.area || "Uten område"; (posGroups[a] = posGroups[a] || []).push(p.name); });
  if (hr.position_title && !posNames.includes(hr.position_title)) { (posGroups["Annet"] = posGroups["Annet"] || []).push(hr.position_title); }
  const positionField = (
    <div><Label className="label-caps">Stillingstittel</Label>
      <Select value={hr.position_title || ""} onValueChange={(v) => setHr({ ...hr, position_title: v })}>
        <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="hr-position_title"><SelectValue placeholder="Velg" /></SelectTrigger>
        <SelectContent>
          {Object.entries(posGroups).map(([area, names]) => (
            <SelectGroup key={area}>
              <SelectLabel>{area}</SelectLabel>
              {names.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select></div>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`employee-card-${user.id}`} className="text-muted-foreground hover:text-foreground" title="Ansattkort"><IdCard className="w-4 h-4" /></button>
      </DialogTrigger>
      <DialogContent className="bg-popover border-border max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Ansattkort · {user.name}</DialogTitle></DialogHeader>

        <div className="space-y-4">
          <div>
            <h4 className="font-medium mb-2 text-sm">Ansettelsesdetaljer</h4>
            <div className="grid grid-cols-2 gap-3">
              {positionField}
              {field("Stillingsbrøk (%)", "position_pct", { type: "number", step: "1" })}
              {field("Årslønn (kr)", "annual_salary", { type: "number" })}
              {field("Tiltredelsesdato", "start_date", { type: "date" })}
              {field("Sluttdato (valgfritt)", "end_date", { type: "date" })}
              {field("Neste lønnsregulering", "salary_review_date", { type: "date" })}
              {field("Feriedager", "vacation_days", { type: "number" })}
              {field("Fødselsnummer", "national_id", { maxLength: 11 })}
              {field("Kontonummer (lønn)", "bank_account")}
              {field("Adresse", "address")}
              {field("Postnummer", "postal_code", { maxLength: 4 })}
              {field("Poststed", "postal_place")}
              {field("Bosted", "residence")}
              {selectField("Avdeling", "department", deps)}
              {selectField("Lokasjon", "location", locs)}
              {field("Privat e-post (avtale sendes hit)", "private_email", { type: "email", placeholder: "privat@epost.no" })}
            </div>
            {emp?.probation && (
              <div className="mt-3 text-sm" data-testid="hr-probation">
                {emp.probation.applicable === false ? (
                  <span className="text-muted-foreground">Prøvetid: <span className="text-foreground font-medium">Ingen prøvetid avtalt</span></span>
                ) : (<>
                  <span className="text-muted-foreground">Prøvetid ({emp.probation.months} mnd): </span>
                  {emp.probation.completed
                    ? <span className="text-emerald-400 font-medium">Gjennomført</span>
                    : <span className="text-amber-400 font-medium">Pågår · utløper {fmtDate(emp.probation.end)}</span>}
                </>)}
              </div>
            )}
            <label className="flex items-center gap-2 text-sm mt-3" data-testid="hr-is-leader">
              <Checkbox checked={!!hr.is_leader} onCheckedChange={(v) => setHr({ ...hr, is_leader: !!v })} />
              Er leder (kan velges som nærmeste leder / motsignatur på arbeidsavtaler)
            </label>
            <h4 className="font-medium mt-4 mb-2 text-sm">Nærmeste pårørende / nødkontakt</h4>
            <div className="grid grid-cols-3 gap-3">
              {field("Navn", "emergency_contact_name")}
              {field("Telefon", "emergency_contact_phone")}
              {field("Relasjon", "emergency_contact_relation")}
            </div>
            <Button onClick={saveHr} disabled={busy} data-testid="hr-save" className="mt-3 bg-white text-black hover:bg-white/90">Lagre ansattkort</Button>
          </div>

          <div className="border-t border-border pt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-sm flex items-center gap-2"><FileText className="w-4 h-4" /> Arbeidsavtaler</h4>
              <GenerateContractDialog userId={user.id} hr={hr} leaders={leaders} onDone={load} />
            </div>
            <p className="text-xs text-muted-foreground mb-3">Avtalen fylles automatisk med navn, stilling, stillingsbrøk, lønn, startdato, adresse m.m. fra ansattkortet. Signeres digitalt av ansatt + valgt leder (motsignatur).</p>

            {contracts.length === 0 ? <p className="text-sm text-muted-foreground">Ingen arbeidsavtaler ennå.</p> : (
              <div className="space-y-3">
                {contracts.map((e) => (
                  <div key={e.id} className="p-3 rounded-lg bg-secondary/50 border border-border" data-testid={`contract-${e.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">{e.contract_id} <span className="text-xs text-muted-foreground">· {SIGN_STATUS[e.sign_status] || e.sign_status}</span></p>
                        <p className="text-xs text-muted-foreground">Opprettet {fmtDate(e.created_at)}{e.leader_name ? ` · Motsignatur: ${e.leader_name}` : ""}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => openDoc(`/employment/${e.id}/preview`)} className="text-muted-foreground hover:text-foreground p-1" title="Forhåndsvis"><Eye className="w-4 h-4" /></button>
                        {e.signed && <button onClick={() => openDoc(`/employment/${e.id}/pdf`)} className="text-muted-foreground hover:text-foreground p-1" title="Last ned PDF"><Download className="w-4 h-4" /></button>}
                        {!e.signed && <button onClick={() => del(e.id)} className="text-muted-foreground hover:text-destructive p-1" title="Slett"><Trash2 className="w-4 h-4" /></button>}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3 mt-2 text-xs" data-testid={`contract-progress-${e.id}`}>
                      <span className={e.employee_signed ? "text-emerald-400" : "text-muted-foreground"}>{e.employee_signed ? "✓" : "○"} Ansatt{e.employee_signer_name ? `: ${e.employee_signer_name}` : ""}</span>
                      <span className={e.leader_signed ? "text-emerald-400" : "text-muted-foreground"}>{e.leader_signed ? "✓" : "○"} Arbeidsgiver{e.leader_signer_name ? `: ${e.leader_signer_name}` : ""}</span>
                      {e.signed && <span className="text-emerald-400 font-medium">· Ferdig signert</span>}
                    </div>

                    {!e.signed && (
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        {e.sign_status === "ikke_opprettet" || e.sign_status === "kladd" ? (
                          <>
                            <Select value={leaderId} onValueChange={setLeaderId}>
                              <SelectTrigger className="h-8 w-52 bg-secondary border-border text-xs" data-testid={`leader-select-${e.id}`}><SelectValue placeholder="Velg leder (motsignatur)" /></SelectTrigger>
                              <SelectContent className="bg-popover border-border">
                                {leaders.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <Button size="sm" onClick={() => send(e.id)} data-testid={`send-contract-${e.id}`} className="h-8 text-xs bg-white text-black hover:bg-white/90"><Send className="w-3.5 h-3.5 mr-1" /> Send til signering</Button>
                          </>
                        ) : (
                          <>
                            <Button size="sm" variant="secondary" onClick={() => send(e.id)} data-testid={`resend-contract-${e.id}`} className="h-8 text-xs"><Send className="w-3.5 h-3.5 mr-1" /> Send lenke på nytt</Button>
                            {e.employee_signed && !e.leader_signed && (
                              <Button size="sm" onClick={() => countersign(e.id)} data-testid={`countersign-${e.id}`} className="h-8 text-xs bg-white text-black hover:bg-white/90"><PenLine className="w-3.5 h-3.5 mr-1" /> Signer som arbeidsgiver</Button>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {!e.employee_signed && e.sign_link_token && (
                      <div className="mt-3 rounded-lg bg-background/60 border border-border p-3 text-xs" data-testid={`sign-link-${e.id}`}>
                        <p className="font-medium mb-1 flex items-center gap-1"><LinkIcon className="w-3.5 h-3.5" /> Signeringslenke for ansatt</p>
                        <p className="text-muted-foreground mb-2">Sendt på e-post. Du kan også åpne signeringssiden direkte her – engangskode: <strong className="text-foreground tracking-widest">{e.sign_code}</strong></p>
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" onClick={() => window.open(`${window.location.origin}/sign/${e.sign_link_token}`, "_blank")} data-testid={`open-sign-${e.id}`} className="h-8 text-xs bg-white text-black hover:bg-white/90"><PenLine className="w-3.5 h-3.5 mr-1" /> Åpne signeringsside</Button>
                          <Button size="sm" variant="secondary" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/sign/${e.sign_link_token}`); toast.success("Lenke kopiert"); }} className="h-8 text-xs">Kopier lenke</Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
